import { getLogger } from 'app-components/utils/core';
import { immutableSerialize } from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';
import { getCacheMetadata } from './cacheMetadata';
import { clearCachedState } from './clearCachedState';
import { type CacheObject, type Config, type ReducerConfig, defaultGetCacheKey } from './config';
import { decryptString } from './encryption';

const log = getLogger('getCachedState.ts');

type RestoredState = { [key: string]: any };

const getConfigForKey = (key: string, config: Config) => config.persistableStateConfig[key];

const getKeyForIndex = (i: number, config: Config) => config.stateKeys[i];

const getConfigForIndex = (i: number, config: Config) => getConfigForKey(getKeyForIndex(i, config), config);

export const defaultCacheToState = <D>(data: D) => {
  let state = immutableSerialize.convertToImmutableRecursive(data);
  // reset isLoading
  if (state.isLoading && state.set) {
    state = state.set('isLoading', false);
  }

  // remove optimistic/ghost records
  if (state.resourcesById && state.resourcesById.set && state.resourcesById.filter) {
    state = state.set(
      'resourcesById',
      state.resourcesById.filter((resource: { id: string }, key: string) => resource.id === key),
    );
  }

  return state;
};

function transformCacheToState(parsedCache: CacheObject, config: ReducerConfig<keyof RootState>, name: string) {
  const { version, data } = parsedCache;

  if (version !== config.version) {
    log.warn(`${name} version mismatch - reset to defaults (${version} -> ${config.version})`);

    return undefined;
  }

  const { cacheToState = defaultCacheToState } = config;
  return cacheToState(data);
}

async function migrateCache(persistConfig: Config) {
  const migrations = persistConfig.stateKeys.map((key) => {
    const config = getConfigForKey(key, persistConfig);

    return config.migrate?.();
  });

  const migratedCaches = await Promise.all(migrations);

  return migratedCaches.reduce((state, cache, index) => {
    const config = getConfigForIndex(index, persistConfig);

    const name = getKeyForIndex(index, persistConfig);

    if (cache) {
      state[name] = transformCacheToState(cache, config, name);
    }

    return state;
  }, {} as RestoredState);
}

function decryptAndParseCache(caches: Array<string | null | undefined>, config: Config) {
  return caches.reduce((state, cached, index) => {
    if (cached) {
      const reducerName = getKeyForIndex(index, config);

      const reducerConfig = getConfigForIndex(index, config);

      const shouldDecrypt = Boolean(reducerConfig.encrypt);

      let cachedString = cached;

      if (shouldDecrypt) {
        cachedString = decryptString(cached, config.encryptionKey);
      }

      const parsed: CacheObject = JSON.parse(cachedString);

      state[reducerName] = transformCacheToState(parsed, reducerConfig, reducerName);
    }

    return state;
  }, {} as RestoredState);
}

export async function getCachedState(config: Config) {
  log.info(`persist version: ${config.version}`);

  const metadata = await getCacheMetadata(config);

  if (metadata.version !== config.version) {
    log.warn(`global version mismatch - migrating cached data (${metadata.version} -> ${config.version})`);

    const migrated = await migrateCache(config);

    await clearCachedState(config);

    return migrated;
  }

  const getCacheKey = config.getCacheKeyForStateKey || defaultGetCacheKey;
  const tasks = config.stateKeys.map((key) => config.storageInterface.getItem(getCacheKey(key)));

  const cachedStates = await Promise.all(tasks);

  return decryptAndParseCache(cachedStates, config);
}
